import { type FC, useState } from "react";

import { useMutation } from "@apollo/client/react";

import {
  EvaluationTypeEnum,
  ProjectResetEvaluationTypeDocument,
  ProjectSetEvaluationTypeDocument,
  Status,
} from "@app_schema";

import { track } from "@application/utilities/track";

import { EvalTypeChoices } from "./eval_type_picker";

const GENERIC_FAILURE = "Couldn't set the evaluation type — please try again.";

// Setup gate for the project detail page: the evaluation type decides how
// records are organized and compared, so it's chosen BEFORE the work UI
// (assistant + intake) appears — not buried in the Records pane. While
// `evaluation_type` is null the page shows only this picker; once set, the
// detail page re-renders into the full working layout.
//
// `← Change` reopens the same picker on a project that already has a type. It
// passes `current`, so the committed type is the one shown selected — the
// picker no longer opens on a hardcoded Comprehensive and tells the clinician
// their case is something it isn't.
export const EvalTypeGate: FC<{
  projectId: string;
  // The committed type when reopened via `← Change`; null on first run, which
  // is the only case that legitimately defaults to Comprehensive.
  current?: EvaluationTypeEnum | null;
  // Closes the picker. Absent on first run — there is nothing to go back to
  // until a type is committed.
  onClose?: () => void;
}> = ({ projectId, current, onClose }) => {
  const [setEvalType, { loading }] = useMutation(
    ProjectSetEvaluationTypeDocument,
  );
  const [resetEvalType, { loading: resetting }] = useMutation(
    ProjectResetEvaluationTypeDocument,
  );
  // BOTH round trips have to disable the cards. Gating on the set alone leaves
  // them live for the whole reset, and a second click there reads the still-
  // uncommitted `current`, so two reset+set pairs race: the later set arrives
  // with a type already committed, which `transition_allowed?` rejects. The
  // user lands on the card they first picked, under a "try again" banner.
  const busy = loading || resetting;
  // Optimistic selection so the chosen card highlights while the mutation +
  // refetch are in flight.
  const [pending, setPending] = useState<EvaluationTypeEnum>(
    current ?? EvaluationTypeEnum.Comprehensive,
  );
  // Without feedback a failed mutation silently strands the user on the gate.
  // A string, not a flag: the two failures need different words — one is worth
  // retrying, the other can never succeed.
  const [error, setError] = useState<string | null>(null);

  const onSelect = async (type: EvaluationTypeEnum) => {
    // Re-picking the committed type is a confirmation, not a change. Close
    // without touching the server: the reset below would otherwise clear the
    // type just to write the same value back.
    if (type === current) {
      onClose?.();
      return;
    }

    setPending(type);
    setError(null);
    try {
      // Post-creation the server locks transitions to Comprehensive →
      // Follow-Up (ProjectSetEvaluationTypeMutation#transition_allowed?), so a
      // committed type is cleared first. Both calls fire only once the user
      // has actually chosen — opening the picker changes nothing.
      if (current) {
        const reset = await resetEvalType({ variables: { id: projectId } });
        const status = reset.data?.project?.resetEvaluationType?.status;
        if (status !== Status.Ok) {
          // Claim the specific cause only where the server actually stated it.
          // UNPROCESSABLE has exactly one trigger — records already exist — and
          // that lock is permanent, so "try again" would be a lie. Anything
          // else reaching here is unexplained, and says so.
          setError(
            status === Status.Unprocessable
              ? "This case already has records — the evaluation type can no longer be changed."
              : GENERIC_FAILURE,
          );
          return;
        }
      }

      const result = await setEvalType({
        variables: { id: projectId, evaluationType: type },
        refetchQueries: ["DashboardProjectsDetails"],
        awaitRefetchQueries: true,
      });
      if (result.data?.project?.setEvaluationType?.errors?.length) {
        setError(GENERIC_FAILURE);
      } else {
        track("evaluation_type_selected", {
          project_id: projectId,
          evaluation_type: type,
        });
        onClose?.();
      }
    } catch {
      setError(GENERIC_FAILURE);
    }
  };

  return (
    <div className="mx-auto max-w-3xl py-10 text-center">
      <div className="text-ink mb-1 text-lg font-semibold tracking-[-0.015em]">
        What type of evaluation is this?
      </div>
      <div className="text-muted text-[13px]">
        This determines how your records are organized and compared.
      </div>
      <EvalTypeChoices selected={pending} onSelect={onSelect} loading={busy} />
      {error && <div className="mt-4 text-[13px] text-rose-600">{error}</div>}
      {onClose && (
        <button
          type="button"
          onClick={onClose}
          disabled={busy}
          className="mt-4 cursor-pointer text-xs text-indigo-500 hover:underline disabled:pointer-events-none"
        >
          Cancel
        </button>
      )}
    </div>
  );
};
